import { api } from '../api.ts';
import { compact, html, mount, raw } from '../html.ts';
import { COMPANY_KIND, formatDate, options, t, tk } from '../i18n.ts';
import { canWrite, invalidate, lookups } from '../state.ts';
import { confirmDialog, emptyState, field, openFormDialog, optionList, select, textarea, toast, toastError } from '../ui.ts';
import { pageHeader } from './common.ts';

const WEEKDAYS = [tk('Mo'), tk('Di'), tk('Mi'), tk('Do'), tk('Fr'), tk('Sa'), tk('So')];
const weekday = (index: number) => t(WEEKDAYS[index]!);
const ABSENCE = (): Record<string, string> => ({ vacation: t('Urlaub'), sick: t('Krank'), other: t('Sonstiges') });

function addDelete(dialog: HTMLDialogElement, title: string, message: string, remove: () => Promise<void>) {
  if (!canWrite()) return;
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'p-button p-button--ghost pma-danger-text';
  button.textContent = t('Löschen');
  button.addEventListener('click', async () => {
    dialog.close();
    if (await confirmDialog(title, message)) await remove().catch(toastError);
  });
  dialog.querySelector('.p-dialog__actions')!.prepend(button);
}

function searchBox(label: string) {
  return html`<label class="p-field pma-search"><span class="p-visually-hidden">${label}</span>
    <input class="p-input" type="search" placeholder="${label}" data-filter></label>`;
}

function wireFilter(container: HTMLElement) {
  const input = container.querySelector<HTMLInputElement>('[data-filter]');
  input?.addEventListener('input', () => {
    const q = input.value.trim().toLowerCase();
    for (const row of container.querySelectorAll<HTMLElement>('[data-search]')) {
      row.hidden = !!q && !row.dataset.search!.includes(q);
    }
  });
}

// ---------------------------------------------------------------------------- contacts

export async function contactsView(container: HTMLElement): Promise<void> {
  const [{ items }, companies] = await Promise.all([api.get('/contacts?limit=500'), lookups.companies()]);
  const companyNames = new Map(companies.map((c) => [c.id, c.label]));

  mount(
    container,
    html`${pageHeader(t('Beziehungen'), t('Kontakte'), t('Menschen hinter Kunden, Lieferanten und Projekten – mit Kernzeiten und Abwesenheiten.'),
      html`${searchBox(t('Kontakte durchsuchen'))}${canWrite() ? html`<button class="p-button p-button--primary" type="button" data-new>${t('Neuer Kontakt')}</button>` : ''}`)}
    ${
      items.length
        ? html`<div class="p-table-wrap"><table class="p-table pma-table"><caption class="p-visually-hidden">${t('Kontakte')}</caption>
            <thead><tr><th scope="col">${t('Name')}</th><th scope="col">${t('Firma')}</th><th scope="col">${t('Position')}</th><th scope="col">${t('E-Mail')}</th><th scope="col">${t('Telefon')}</th><th scope="col">${t('Kernzeiten')}</th></tr></thead>
            <tbody>${items.map((c: any) => {
              const hours = Object.entries(c.working_hours ?? {}) as [string, [string, string]][];
              return html`<tr data-search="${`${c.first_name} ${c.last_name} ${c.email ?? ''} ${companyNames.get(c.company_id) ?? ''}`.toLowerCase()}">
                <td data-label="${t('Name')}"><button type="button" class="p-button p-button--link pma-left" data-edit="${c.id}">${c.title ? `${c.title} ` : ''}${c.first_name} ${c.last_name}</button></td>
                <td data-label="${t('Firma')}">${companyNames.get(c.company_id) ?? '–'}</td>
                <td data-label="${t('Position')}">${c.position ?? '–'}</td>
                <td data-label="${t('E-Mail')}">${c.email ? html`<a href="mailto:${c.email}">${c.email}</a>` : '–'}</td>
                <td data-label="${t('Telefon')}">${c.phone ? html`<a href="tel:${c.phone}">${c.phone}</a>` : '–'}</td>
                <td data-label="${t('Kernzeiten')}" class="pma-small">${hours.length ? hours.sort().map(([d, [from, to]]) => `${weekday(Number(d) - 1)} ${from}–${to}`).join(', ') : '–'}</td>
              </tr>`;
            })}</tbody></table></div>`
        : emptyState(t('Noch keine Kontakte'), t('Kontakte lassen sich Projekten als Stakeholder zuordnen, Aufgaben delegieren und Risiken zuweisen.'))
    }`,
  );
  wireFilter(container);

  async function edit(contact: any | null) {
    const hours: Record<string, [string, string]> = contact?.working_hours ?? {};
    let absences: any[] = contact ? (await api.get(`/contacts/${contact.id}/absences`)).items : [];
    const dialog = openFormDialog({
      title: contact ? `${contact.first_name} ${contact.last_name}` : t('Neuer Kontakt'),
      wide: true,
      fields: html`${field(t('Titel'), 'title', 'text', { value: contact?.title, placeholder: t('Dr., Mag., …') })}
        ${field(t('Vorname'), 'first_name', 'text', { required: true, value: contact?.first_name })}
        ${field(t('Nachname'), 'last_name', 'text', { required: true, value: contact?.last_name })}
        ${select(t('Firma'), 'company_id', optionList(companies, contact?.company_id, t('Keine Firma')))}
        ${field(t('Position'), 'position', 'text', { value: contact?.position })}
        ${field(t('E-Mail'), 'email', 'email', { value: contact?.email })}
        ${field(t('Telefon'), 'phone', 'tel', { value: contact?.phone })}
        <fieldset class="p-fieldset pma-span-2 pma-hours"><legend class="p-label">${t('Kernarbeitszeiten')}</legend>
          ${WEEKDAYS.map(
            (_, i) => html`<div class="pma-hours__row"><span>${weekday(i)}</span>
              <input class="p-input" data-p-size="sm" type="time" name="from_${i + 1}" value="${hours[String(i + 1)]?.[0] ?? ''}" aria-label="${t('{day} von', { day: weekday(i) })}">
              <input class="p-input" data-p-size="sm" type="time" name="to_${i + 1}" value="${hours[String(i + 1)]?.[1] ?? ''}" aria-label="${t('{day} bis', { day: weekday(i) })}"></div>`,
          )}
        </fieldset>
        ${
          contact
            ? html`<fieldset class="p-fieldset pma-span-2"><legend class="p-label">${t('Abwesenheiten')}</legend>
                <ul class="pma-list" data-absences>${absences.map(
                  (a) => html`<li class="pma-list__row"><span>${ABSENCE()[a.kind]} · ${formatDate(a.starts_on)} – ${formatDate(a.ends_on)}${a.note ? ` · ${a.note}` : ''}</span>
                    ${canWrite() ? html`<button type="button" class="p-button p-button--ghost p-button--sm" data-remove-absence="${a.id}">${t('Entfernen')}</button>` : ''}</li>`,
                )}</ul>
                ${canWrite() ? html`<div class="pma-inline-add">
                  <select class="p-select" data-p-size="sm" data-absence-kind aria-label="${t('Art')}">${raw(options(ABSENCE()))}</select>
                  <input class="p-input" data-p-size="sm" type="date" data-absence-from aria-label="${t('Von')}">
                  <input class="p-input" data-p-size="sm" type="date" data-absence-to aria-label="${t('Bis')}">
                  <button type="button" class="p-button p-button--sm" data-add-absence>${t('Hinzufügen')}</button></div>` : ''}
              </fieldset>`
            : ''
        }
        ${textarea(t('Notizen'), 'notes', { value: contact?.notes, rows: 3 })}`,
      onSubmit: async (data) => {
        const workingHours: Record<string, [string, string]> = {};
        for (let d = 1; d <= 7; d++) {
          const from = data[`from_${d}`];
          const to = data[`to_${d}`];
          delete data[`from_${d}`];
          delete data[`to_${d}`];
          if (typeof from === 'string' && typeof to === 'string') workingHours[String(d)] = [from, to];
        }
        data.working_hours = workingHours;
        if (contact) await api.patch(`/contacts/${contact.id}`, data);
        else await api.post('/contacts', compact(data));
        invalidate('contacts');
        toast(t('Kontakt gespeichert'));
        await contactsView(container);
      },
    });
    dialog.addEventListener('click', async (event) => {
      const element = event.target as HTMLElement;
      const removeId = element.closest<HTMLElement>('[data-remove-absence]')?.dataset.removeAbsence;
      if (removeId) {
        await api.delete(`/absences/${removeId}`).catch(toastError);
        element.closest('li')?.remove();
      }
      if (element.closest('[data-add-absence]')) {
        const kind = dialog.querySelector<HTMLSelectElement>('[data-absence-kind]')!.value;
        const from = dialog.querySelector<HTMLInputElement>('[data-absence-from]')!.value;
        const to = dialog.querySelector<HTMLInputElement>('[data-absence-to]')!.value || from;
        if (!from) return;
        try {
          const created = await api.post(`/contacts/${contact.id}/absences`, { kind, starts_on: from, ends_on: to });
          absences = [...absences, created];
          dialog.querySelector('[data-absences]')!.insertAdjacentHTML(
            'beforeend',
            html`<li class="pma-list__row"><span>${formatDate(created.starts_on)} – ${formatDate(created.ends_on)}</span>
              <button type="button" class="p-button p-button--ghost p-button--sm" data-remove-absence="${created.id}">${t('Entfernen')}</button></li>`.value,
          );
        } catch (error) {
          toastError(error);
        }
      }
    });
    if (contact) {
      addDelete(dialog, t('Kontakt löschen?'), t('{name} wird gelöscht und aus allen Projekten entfernt.', { name: `${contact.first_name} ${contact.last_name}` }), async () => {
        await api.delete(`/contacts/${contact.id}`);
        invalidate('contacts');
        await contactsView(container);
      });
    }
  }

  container.onclick = (event) => {
    const element = event.target as HTMLElement;
    if (element.closest('[data-new]')) void edit(null);
    const id = element.closest<HTMLElement>('[data-edit]')?.dataset.edit;
    if (id) void edit(items.find((c: any) => c.id === id));
  };
}

// ---------------------------------------------------------------------------- companies

export async function companiesView(container: HTMLElement): Promise<void> {
  const { items } = await api.get('/companies?limit=500');
  mount(
    container,
    html`${pageHeader(t('Beziehungen'), t('Firmen'), t('Kunden, Lieferanten und Partner.'),
      html`${searchBox(t('Firmen durchsuchen'))}${canWrite() ? html`<button class="p-button p-button--primary" type="button" data-new>${t('Neue Firma')}</button>` : ''}`)}
    ${
      items.length
        ? html`<div class="p-table-wrap"><table class="p-table pma-table"><caption class="p-visually-hidden">${t('Firmen')}</caption>
            <thead><tr><th scope="col">${t('Name')}</th><th scope="col">${t('Art')}</th><th scope="col">${t('Ort')}</th><th scope="col">${t('Web')}</th><th scope="col">${t('E-Mail')}</th></tr></thead>
            <tbody>${items.map(
              (c: any) => html`<tr data-search="${`${c.name} ${c.city ?? ''}`.toLowerCase()}">
                <td data-label="${t('Name')}"><button type="button" class="p-button p-button--link pma-left" data-edit="${c.id}">${c.name}</button></td>
                <td data-label="${t('Art')}">${COMPANY_KIND[c.kind]}</td>
                <td data-label="${t('Ort')}">${[c.postal_code, c.city].filter(Boolean).join(' ') || '–'}${c.country ? `, ${c.country}` : ''}</td>
                <td data-label="${t('Web')}">${c.website && /^https?:\/\//.test(c.website) ? html`<a href="${c.website}" rel="noopener noreferrer" target="_blank">${c.website.replace(/^https?:\/\//, '')}</a>` : c.website ?? '–'}</td>
                <td data-label="${t('E-Mail')}">${c.email ?? '–'}</td>
              </tr>`,
            )}</tbody></table></div>`
        : emptyState(t('Noch keine Firmen'), t('Firmen werden Projekten als Kunde zugeordnet und Kontakten als Arbeitgeber.'))
    }`,
  );
  wireFilter(container);

  function edit(company: any | null) {
    const dialog = openFormDialog({
      title: company ? company.name : t('Neue Firma'),
      wide: true,
      fields: html`${field(t('Name'), 'name', 'text', { required: true, value: company?.name, span: true })}
        ${select(t('Art'), 'kind', options(COMPANY_KIND, company?.kind ?? 'client'))}
        ${field(t('Website'), 'website', 'url', { value: company?.website, placeholder: 'https://' })}
        ${field(t('E-Mail'), 'email', 'email', { value: company?.email })}
        ${field(t('Telefon'), 'phone', 'tel', { value: company?.phone })}
        ${field(t('Straße'), 'street', 'text', { value: company?.street, span: true })}
        ${field(t('PLZ'), 'postal_code', 'text', { value: company?.postal_code })}
        ${field(t('Ort'), 'city', 'text', { value: company?.city })}
        ${field(t('Land'), 'country', 'text', { value: company?.country })}
        ${field(t('UID-Nummer'), 'vat_id', 'text', { value: company?.vat_id })}
        ${textarea(t('Notizen'), 'notes', { value: company?.notes, rows: 3 })}`,
      onSubmit: async (data) => {
        if (company) await api.patch(`/companies/${company.id}`, data);
        else await api.post('/companies', compact(data));
        invalidate('companies');
        toast(t('Firma gespeichert'));
        await companiesView(container);
      },
    });
    if (company) {
      addDelete(dialog, t('Firma löschen?'), t('{name} wird gelöscht. Projekte und Kontakte verlieren die Zuordnung.', { name: company.name }), async () => {
        await api.delete(`/companies/${company.id}`);
        invalidate('companies');
        await companiesView(container);
      });
    }
  }

  container.onclick = (event) => {
    const element = event.target as HTMLElement;
    if (element.closest('[data-new]')) edit(null);
    const id = element.closest<HTMLElement>('[data-edit]')?.dataset.edit;
    if (id) edit(items.find((c: any) => c.id === id));
  };
}
